import { memo } from 'react';
import { cx } from '@emotion/css';
import { ChevronRightRounded as ChevronRightIcon } from '@mui/icons-material';

import { Heading, ProgressBar } from '_ui';
import { Chip } from '_ds';

import { getEnv } from '../global/config/env';
import { useI18n } from '../global/hooks/i18n';
import { useLocale } from '../global/hooks/locale';

import cn from './card.module.scss';

import WcsHeadTag from '../assets/universes/wcs/headtag.svg';
import SpicedHeadTag from '../assets/universes/spiced/headtag.png';
import NfHeadTag from '../assets/universes/nf/headtag.png';
import SimplonHeadTag from '../assets/universes/simplon/headtag.avif';
import FoundationHeadTag from '../assets/universes/foundation/headtag.avif';

type CardProps = {
    ctaLabel: string;
    goToTraining?: () => void | Promise<void>;
    trainingUrl?: string;
    isAvailable: boolean;
    loading: boolean;
    name: string;
    trainingType: string;
    color: string;
    track: string;
    headTag?: React.ReactNode;
    estimatedTime?: number;
    startsOn?: Date;
    progression?: number;
    droppedOutAt?: Date;
}

export const HeadTag = ({ universe }: { universe: string }) => {

    if (universe === 'nf') return (<img src={NfHeadTag} alt="NF" />);
    if (universe === 'spiced') return <img src={SpicedHeadTag} alt="Spiced" />;
    if (universe === 'simplon') return <img src={SimplonHeadTag} alt="Simplon" />;
    if (universe === 'foundation') return <img src={FoundationHeadTag} alt="Simplon Foundation" />;

    return <WcsHeadTag />;
};

const Card = ({ ctaLabel, goToTraining, trainingUrl, isAvailable, name, trainingType, color, track, startsOn, progression, droppedOutAt }: CardProps) => {

    const t = useI18n('card');
    const locale = useLocale();

    const env = getEnv();
    const { universe } = env;

    const { Comp, ...props } = (() => {
        if (!isAvailable) return {
            Comp: 'div' as React.ElementType,
        };
        if (trainingUrl) return {
            Comp: 'a' as React.ElementType,
            href: trainingUrl,
            'aria-label': t(`startTraining.${ctaLabel}`),
        };
        return {
            Comp: 'button' as React.ElementType,
            onClick: goToTraining,
            'aria-label': t(`startTraining.${ctaLabel}`),
        };
    })();

    return (
        <Comp className={cx(cn.card, `training--${color}`, isAvailable && cn.available)} {...props}>
            <div className={cx(cn.headTag, cn[universe])}>
                <HeadTag universe={universe} />
            </div>

            <div className={cn.contentBody}>
                <div className={cn.metas}>
                    <div className={cn.track}>
                        {track}
                    </div>
                    <div className={cn.typenTime}>
                        <span className={cn.type}>{t(`trainingTypes.${trainingType}`)}</span>
                        {startsOn && <Chip label={t('startsOn', { date: new Date(startsOn).toLocaleDateString(locale) })} severity="warning" />}
                        {droppedOutAt && <Chip label={t('droppedOutAt', { date: new Date(droppedOutAt).toLocaleDateString(locale) })} severity="neutral" />}
                    </div>
                </div>
                <div className={cn.training}>
                    <Heading heading="h4" className={cn.name}>{name}</Heading>
                </div>
                <div className={cn.progression}>
                    {Number.isInteger(progression) && <ProgressBar progress={progression} />}
                </div>
                <div className={cn.cardButton}>
                    <ChevronRightIcon />
                </div>
            </div>

        </Comp>
    );
};

export default memo(Card);
